import React from 'react';
import { useParams } from 'react-router-dom';
import LibModal from '../../../features/LibModal/LibModal';
import NotFound from '../../../features/NotFound/NotFoundPage';
import InputField from '../../../utils/InputField';
import PageTitle from '../../../utils/PageTitle';
import { GetSearchParams } from '../../../utils/URL';
import cn from '../../../utils/cn';
import SubscriptionsProvider, { useSubscriptions } from '../SubscriptionsProvider';
import LinkUpCard, { LinkWithGithubCard, RenewCard } from './Components/LinkUpCard';
import SubscriptionTableTab from './Components/SubscriptionTableTab';
import SubscriptionsDetailsHeader from './Components/SubscriptionsDetailsHeader';
import SubscriptionInvoicesPage from './Components/SubscriptionsTabTables/Invoices/SubscriptionInvoicesPage';
import SubscriptionsLogsPage from './Components/SubscriptionsTabTables/Logs/SubscriptionsLogsPage';
import SubscriptionPaymentsPage from './Components/SubscriptionsTabTables/Payments/SubscriptionPaymentsPage';
import SubscriptionsServicesPage from './Components/SubscriptionsTabTables/Services/SubscriptionsServicesPage';
import SubscriptionsSupportTicketsPage from './Components/SubscriptionsTabTables/SupportTicket/SubscriptionsSupportTicketsPage';
import AddGithubLinkUpModal from './Modals/AddGithubLinkUpModal';
import AddAppFeatureModal from './Modals/AddSubscriptionsFeatureModal';
import ChangeBasicInfoModal from './Modals/ChangeBasicInfoModal';
import EditSubscriptionsFeatureModal from './Modals/EditSubscriptionsFeatureModal';
import KeyGenerationModal from './Modals/KeyGenerationModal';
import ModifyVersionInfo from './Modals/ModifyVersionInfo';
import SubsStartDateModifyModal from './Modals/SubsStartDateModifyModal';
import SubscriptionRenewModal from './Modals/SubscriptionRenewModal';
import ViewGithubReleases from './Modals/ViewGithubReleases';
import ViewAppFeatureModal from './Modals/ViewSubscriptionsFeatureModal';
import SubscriptionsDetailsProvider, {
    useSubscriptionsDetails
} from './SubscriptionsDetailsProvider';

function HeaderWithGithubRepoSearch({ githubRepoInputData, setGithubRepoInputData }) {
    const handleOnChange = (ev) => {
        ev?.preventDefault();
        const { name, value } = ev.target;

        if (name) {
            setGithubRepoInputData((d) => ({
                ...d,
                Search: value
            }));
        }
    };

    return (
        <div className="flex items-center gap-3">
            <div>Config with Github Repository </div>
            <InputField
                placeholder="Search Github Repo"
                name="Search"
                formData={githubRepoInputData}
                onChange={handleOnChange}
                className="w-48 h-8"
            />
        </div>
    );
}

function TabComponentRender({ data }) {
    const tabName = GetSearchParams('tab') || 'logs';

    switch (tabName) {
        case 'logs':
            return <SubscriptionsLogsPage data={data?.subscriptionLogs || []} />;
        case 'services':
            return <SubscriptionsServicesPage data={data?.services || []} />;
        case 'invoices':
            return <SubscriptionInvoicesPage data={data?.invoices || []} />;
        case 'payments':
            return <SubscriptionPaymentsPage data={data?.payments || []} />;
        case 'support-ticket':
            return <SubscriptionsSupportTicketsPage data={data?.supportTickets || []} />;
        default:
            return null;
    }
}

function PageCont({ subscriptionId }) {
    const {
        openAppFeaturesModal,
        setOpenAppFeaturesModal,
        showAddAppFeaturesModal,
        handleAddedNewFeature
    } = useSubscriptions();

    const {
        featureEditModal,
        subscriptionsDetailsData,
        handleReload,
        closeFeatureEditModal,
        openModal,
        closeModal,
        modalType,
        openLinkWithGithubModal,
        setOpenLinkWithGithubModal,
        openGithubReleasesView,
        setOpenGithubReleasesView
    } = useSubscriptionsDetails();
    // if no data found
    if (subscriptionsDetailsData && Object.keys(subscriptionsDetailsData).length === 0) {
        return (
            <div className="inner_page_cont">
                <NotFound
                    title="No Subscription Found!"
                    btnTitle="Back to list"
                    path="/subscriptions"
                    className="h-full"
                />
            </div>
        );
    }
    return (
        <div className="subscription_details_page inner_page_cont">
            <SubscriptionsDetailsHeader />
            <div
                className={cn('mt-5 grid grid-cols-1 sm:grid-cols-2 lg:flex gap-4 mb-7 ', {
                    'placeholder-mode': subscriptionsDetailsData.demo
                })}
            >
                <RenewCard onClick={() => openModal('renew')} data={subscriptionsDetailsData} />
                {subscriptionsDetailsData?.repository?.fullName ? (
                    <LinkWithGithubCard
                        onClick={() => setOpenGithubReleasesView({ show: true, data: '' })}
                        data={subscriptionsDetailsData}
                    />
                ) : (
                    <LinkUpCard
                        title="Link with Github Repo"
                        des="Must be a partial name match. and must be in the release build file."
                        className="bg-purple-600"
                        onClick={() => setOpenLinkWithGithubModal({ show: true, Search: '' })}
                    />
                )}
                <LinkUpCard
                    title="Generate License Key"
                    des="Generate a license key to activate the application on your device."
                    className="bg-purple-600"
                    onClick={() => openModal('key-generation')}
                />
            </div>
            <SubscriptionTableTab data={subscriptionsDetailsData} />

            <TabComponentRender data={subscriptionsDetailsData} />

            {/* View this app all feature's  */}
            {openAppFeaturesModal && (
                <LibModal
                    title="App Features"
                    setOpen={setOpenAppFeaturesModal}
                    className="!w-[900px]"
                >
                    <ViewAppFeatureModal features={subscriptionsDetailsData?.features} />
                </LibModal>
            )}
            {/* New Feature add for this app modal control */}
            {showAddAppFeaturesModal && (
                <LibModal title="Add App Features" setOpen={handleAddedNewFeature}>
                    <AddAppFeatureModal
                        appId={subscriptionId}
                        handleReload={handleReload}
                        handleAddedNewFeature={handleAddedNewFeature}
                    />
                </LibModal>
            )}

            {/* Open Renew */}
            {modalType === 'renew' && (
                <LibModal title="Renew Subscription" setOpen={closeModal}>
                    <SubscriptionRenewModal
                        data={subscriptionsDetailsData}
                        closeModal={() => closeModal(false)}
                    />
                </LibModal>
            )}

            {/* Change Subscriptions basic info */}
            {modalType === 'change-basic-info' && (
                <LibModal title="Change Basic Information" setOpen={closeModal}>
                    <ChangeBasicInfoModal
                        data={subscriptionsDetailsData}
                        closeModal={() => closeModal(false)}
                    />
                </LibModal>
            )}

            {/* edit product information */}
            {modalType === 'edit-subscription' && (
                <LibModal title="Edit Subscription Info" setOpen={closeModal}>
                    <ChangeBasicInfoModal
                        data={subscriptionsDetailsData}
                        handleReload={handleReload}
                        closeModal={closeModal}
                    />
                </LibModal>
            )}
            {/* Key Generations modal  */}
            {modalType === 'key-generation' && (
                <LibModal title="License Token" setOpen={closeModal}>
                    <KeyGenerationModal
                        data={subscriptionsDetailsData}
                        closeModal={() => closeModal(false)}
                    />
                </LibModal>
            )}

            {/* Modify Version */}
            {modalType === 'modify-version' && (
                <LibModal title="Modify Version" setOpen={closeModal}>
                    <ModifyVersionInfo
                        data={subscriptionsDetailsData}
                        closeModal={() => closeModal(false)}
                    />
                </LibModal>
            )}
            {/* Subscription Start Date modify */}
            {modalType === 'modify-start-date' && (
                <LibModal title="Subscription Start Date" setOpen={closeModal} className="md:w-100">
                    <SubsStartDateModifyModal
                        data={subscriptionsDetailsData}
                        closeModal={() => closeModal(false)}
                    />
                </LibModal>
            )}
            {/* Feature Edit modal in main modal */}
            {featureEditModal?.show && (
                <LibModal title="Feature Update" setOpen={closeFeatureEditModal}>
                    <EditSubscriptionsFeatureModal
                        data={featureEditModal?.data}
                        handleReload={handleReload}
                        closeModal={closeFeatureEditModal}
                    />
                </LibModal>
            )}
            {/* Link With Github */}
            {openLinkWithGithubModal?.show && (
                <LibModal
                    title={
                        <HeaderWithGithubRepoSearch
                            githubRepoInputData={openLinkWithGithubModal}
                            setGithubRepoInputData={setOpenLinkWithGithubModal}
                        />
                    }
                    setOpen={setOpenLinkWithGithubModal}
                    className="md:!w-[900px]"
                >
                    <AddGithubLinkUpModal
                        subscriptionId={subscriptionId}
                        openLinkWithGithubModal={openLinkWithGithubModal}
                    />
                </LibModal>
            )}
            {/* Github Releases View */}
            {openGithubReleasesView?.show && (
                <LibModal
                    title="Github Releases"
                    setOpen={setOpenGithubReleasesView}
                    className="md:!w-[750px]"
                >
                    <ViewGithubReleases />
                </LibModal>
            )}
        </div>
    );
}
function SubscriptionsDetailsPage() {
    const { id } = useParams();

    // set page title
    PageTitle(`Subscription Info - ${id}`);

    return (
        <SubscriptionsProvider>
            <SubscriptionsDetailsProvider subscriptionId={id}>
                <PageCont subscriptionId={id} />
            </SubscriptionsDetailsProvider>
        </SubscriptionsProvider>
    );
}

export default SubscriptionsDetailsPage;
